import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { publicGet } from "../admin/api";
import CapabilityBadges from "../components/brand/CapabilityBadges";
import { setSeo } from "../lib/seo";
import { RUNS_LABEL, STATUS_TONE } from "./Servers";

export default function ServerProfile() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setS(null); setMissing(false);
    publicGet(`/public/servers/${encodeURIComponent(slug)}`).then((r) => setS(r.server)).catch(() => setMissing(true));
  }, [slug]);
  useEffect(() => {
    if (!s) return;
    const url = `https://herdsafe.ai/servers/${s.slug}`;
    setSeo({
      title: `${s.name} — MCP server profile — HerdSafe`, url,
      description: `${s.what_it_does} ${s.status_text}. Last checked ${(s.last_checked || "").slice(0, 10)}.`.slice(0, 300),
      jsonLd: {
        "@context": "https://schema.org", "@type": "SoftwareApplication", name: s.name, applicationCategory: "DeveloperApplication",
        description: s.what_it_does, url, ...(s.publisher ? { author: { "@type": "Organization", name: s.publisher } } : {}),
        dateModified: s.last_checked || undefined,
      },
    });
  }, [s]);
  if (missing) return (
    <section data-testid="server-missing" className="space-y-4">
      <h1 className="hs-display text-3xl">Profile not found</h1>
      <p className="text-muted">There is no published profile at this address.</p>
      <Link className="hs-link" to="/servers">← All server profiles</Link>
    </section>);
  if (!s) return <div className="text-muted">Loading…</div>;
  const correction = `/appeal?item_type=${encodeURIComponent(s.correction.item_type || "mcp_server")}&item_identifier=${encodeURIComponent(s.correction.item_identifier || s.name)}&correction=1`;
  const row = (label, value, testid) => (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="m-0" data-testid={testid}>{value}</dd>
    </>);
  return (
    <article data-testid="server-profile" className="space-y-6">
      <Link className="hs-link text-sm" to="/servers">← All server profiles</Link>
      <h1 className="hs-display text-3xl md:text-4xl break-all"><span className="hs-mono">{s.name}</span></h1>
      <div className="flex flex-wrap gap-2 items-center">
        <span className={`hs-badge hs-badge-${STATUS_TONE[s.status] || "neutral"}`} data-testid="server-status">{s.status_text}</span>
        <span className="hs-hint" data-testid="server-last-checked">Last checked {(s.last_checked || "").slice(0, 10) || "—"}</span>
      </div>
      <p className="text-lg hs-prose" data-testid="server-what">{s.what_it_does}</p>
      <p className="text-muted" data-testid="server-disclaimer">{s.disclaimer}</p>

      <section className="hs-card space-y-3">
        <h2 className="hs-display text-xl m-0">Facts</h2>
        <dl className="grid gap-x-6 gap-y-2" style={{ gridTemplateColumns: "minmax(140px, max-content) 1fr" }}>
          {row("Runs", <>{RUNS_LABEL[s.runs_where] || s.runs_where}. {s.runs_where_text}</>, "server-runs")}
          {s.remote_hosts.length > 0 && row("Remote host", s.remote_hosts.map((h) => <code key={h} className="hs-mono mr-2">{h}</code>))}
          {s.packages.length > 0 && row("Packages", s.packages.map((p) => <div key={p.ref}><code className="hs-mono">{p.ref}</code>{p.version && ` · ${p.version}`}</div>))}
          {row("Publisher", <>{s.publisher || "—"}<div className="hs-hint">{s.publisher_verification}</div></>, "server-publisher")}
          {row("Secrets it asks for", s.secrets_needed.length ? s.secrets_needed.map((n) => <code key={n} className="hs-mono mr-2">{n}</code>) : "none", "server-secrets")}
          {row("Broad access", s.capabilities.length ? <CapabilityBadges capabilities={s.capabilities} /> : "none detected", "server-capabilities")}
          {row("Domains its code contacts", s.contacted_domains.length ? s.contacted_domains.map((d) => <code key={d} className="hs-mono mr-2">{d}</code>) : "none recorded", "server-domains")}
          {row("Versions tracked", s.versions_tracked ?? "—")}
        </dl>
      </section>

      <section className="hs-card space-y-3" data-testid="server-risks">
        <h2 className="hs-display text-xl m-0">If compromised, and how to reduce the risk</h2>
        <ul className="space-y-3 m-0 p-0 list-none">
          {s.risks.map((r) => (
            <li key={r.capability} data-testid={`risk-${r.capability}`}>
              <strong>{r.label}.</strong> If compromised: {r.if_compromised} To reduce risk: {r.to_reduce_risk}
            </li>))}
        </ul>
      </section>

      <p className="text-sm">Something wrong here? <a className="hs-link" data-testid="server-correction" href={correction}>Request a correction</a>.</p>
    </article>
  );
}
